import { useCallback, useRef, useState } from "react";
import { useApiClient } from "@src/packages/api/louise-api";
import {
  attachUploadJobSourceUpload,
  createUploadJob,
  createUploadJobIdempotencyKey,
  listUploadJobs,
} from "./uploadJobActivity";
import type {
  UploadDestination,
  UploadJobSourceContext,
} from "./uploadJobContract";
import {
  waitForDocumentProcessingOutcome,
  waitForDocumentSourceIdentity,
  type DocumentProcessingOutcome,
} from "./documentProcessingRun";
import {
  useCompleteUpload,
  useGetUploadUrl,
  useUploadFile,
} from "@src/packages/documents/hooks/useDocuments";
import type { Document } from "@src/packages/documents/hooks/useDocuments";
import {
  readFileForUpload,
  resolveUploadContentType,
} from "@src/packages/utils/fileUpload";

export type DocumentSourceFile = {
  uri: string;
  name: string;
  mimeType?: string | null;
  size?: number;
};

export type FiledDocumentOptions = {
  name: string;
  tags: string[];
  documentDate?: string | null;
  sharedWith?: Document["sharedWith"];
  destination?: UploadDestination;
  sourceContext?: UploadJobSourceContext;
};

export type UploadedDocumentSource = {
  file: DocumentSourceFile;
  uploadId: string;
  jobId?: string;
  fileItemId?: string;
};

export type DocumentSourceTransferState =
  "idle" | "uploading" | "uploaded" | "filing" | "complete" | "failed";

export type DocumentSourceTransferSnapshot = {
  state: DocumentSourceTransferState;
  uploadId: string | null;
  documentId: string | null;
  error: Error | null;
};

/**
 * Shared foreground source transfer for contextual upload surfaces.
 *
 * The source is created in the durable upload-job system before its bytes are
 * acknowledged. Filing now resolves the server-created evidence root from the
 * exact job item; it never creates a second patient document in the client.
 */
export function useDocumentSourceTransfer() {
  const { client, patientId } = useApiClient();
  const getUploadUrl = useGetUploadUrl();
  const uploadFile = useUploadFile();
  const completeUpload = useCompleteUpload();
  const sourceRef = useRef<UploadedDocumentSource | null>(null);
  const sourcesByUploadIdRef = useRef(
    new Map<string, UploadedDocumentSource>(),
  );
  const fileOptionsRef = useRef<FiledDocumentOptions | null>(null);
  const [snapshot, setSnapshot] = useState<DocumentSourceTransferSnapshot>({
    state: "idle",
    uploadId: null,
    documentId: null,
    error: null,
  });

  const resolveSource = useCallback(
    async (source: UploadedDocumentSource): Promise<UploadedDocumentSource> => {
      if (!patientId) {
        throw new Error("Could not identify the patient upload owner");
      }

      let resolvedSource =
        source.jobId && source.fileItemId
          ? source
          : sourcesByUploadIdRef.current.get(source.uploadId);
      if (!resolvedSource?.jobId || !resolvedSource.fileItemId) {
        const jobs = await listUploadJobs(client, patientId, {
          includeCompleted: true,
        });
        const item = jobs.jobs
          .flatMap((job) => job.items)
          .find((candidate) => candidate.uploadId === source.uploadId);
        if (item) {
          resolvedSource = {
            file: source.file,
            uploadId: source.uploadId,
            jobId: item.jobId,
            fileItemId: item.id,
          };
          sourcesByUploadIdRef.current.set(source.uploadId, resolvedSource);
        }
      }
      if (!resolvedSource?.jobId || !resolvedSource.fileItemId) {
        throw new Error("The uploaded document source cannot be resumed");
      }
      sourceRef.current = resolvedSource;
      return resolvedSource;
    },
    [client, patientId],
  );

  const uploadSource = useCallback(
    async (
      file: DocumentSourceFile,
      sourceOptions: Pick<
        FiledDocumentOptions,
        "destination" | "sourceContext"
      > = {},
    ): Promise<UploadedDocumentSource> => {
      if (!patientId) {
        throw new Error("Could not identify the patient upload owner");
      }
      setSnapshot({
        state: "uploading",
        uploadId: null,
        documentId: null,
        error: null,
      });

      try {
        const destination = sourceOptions.destination ?? "document";
        const sourceContext: UploadJobSourceContext = {
          surface: "document_source_transfer",
          ...sourceOptions.sourceContext,
          // The caller's destination is context for prefill/routing only; the
          // acknowledged source remains the durable unit that the activity
          // surface and background processor own.
          targetHints: [
            ...(sourceOptions.sourceContext?.targetHints ?? []),
            destination,
          ],
        };
        const job = await createUploadJob(client, patientId, {
          clientRequestId: createUploadJobIdempotencyKey(),
          source: "picker",
          sourceChannel: "patient_ui",
          intent: "dump_anything",
          sourceContext,
          items: [
            {
              name: file.name,
              contentType: resolveUploadContentType(file),
              sizeBytes: file.size ?? null,
              destination,
            },
          ],
        });
        const item = job.items[0];
        if (!item) throw new Error("Upload job did not return a file item");

        const contentType = resolveUploadContentType(file);
        const { uploadId, uploadUrl } = await getUploadUrl.mutateAsync({
          contentType,
          name: file.name,
        });
        const fileData = await readFileForUpload({
          uri: file.uri,
          name: file.name,
          mimeType: contentType,
        });
        await uploadFile.mutateAsync({
          uploadUrl,
          file: fileData,
          contentType,
        });
        await completeUpload.mutateAsync(uploadId);
        await attachUploadJobSourceUpload(client, patientId, job.id, item.id, {
          uploadId,
        });

        const source = {
          file,
          uploadId,
          jobId: job.id,
          fileItemId: item.id,
        };
        sourceRef.current = source;
        sourcesByUploadIdRef.current.set(uploadId, source);
        setSnapshot({
          state: "uploaded",
          uploadId,
          documentId: null,
          error: null,
        });
        return source;
      } catch (error) {
        const normalizedError =
          error instanceof Error ? error : new Error("Upload failed");
        setSnapshot((current) => ({
          ...current,
          state: "failed",
          error: normalizedError,
        }));
        throw normalizedError;
      }
    },
    [client, completeUpload, getUploadUrl, patientId, uploadFile],
  );

  const fileSource = useCallback(
    async (
      source: UploadedDocumentSource,
      options: FiledDocumentOptions,
    ): Promise<string> => {
      if (!patientId) {
        throw new Error("Could not identify the patient upload owner");
      }
      fileOptionsRef.current = options;
      setSnapshot((current) => ({
        ...current,
        state: "filing",
        uploadId: source.uploadId,
        error: null,
      }));

      try {
        const resolvedSource = await resolveSource(source);
        const { patientDocumentId } = await waitForDocumentSourceIdentity(
          client,
          patientId,
          resolvedSource.jobId!,
          resolvedSource.fileItemId!,
        );
        setSnapshot({
          state: "complete",
          uploadId: source.uploadId,
          documentId: patientDocumentId,
          error: null,
        });
        return patientDocumentId;
      } catch (error) {
        const normalizedError =
          error instanceof Error ? error : new Error("Could not save file");
        setSnapshot((current) => ({
          ...current,
          state: "failed",
          uploadId: source.uploadId,
          error: normalizedError,
        }));
        throw normalizedError;
      }
    },
    [patientId, resolveSource, client],
  );

  const processSource = useCallback(
    async (
      source: UploadedDocumentSource,
      options: { attempts?: number; intervalMs?: number } = {},
    ): Promise<
      DocumentProcessingOutcome & {
        patientDocumentId: string;
        sourceEntityId: string | null;
      }
    > => {
      if (!patientId) {
        throw new Error("Could not identify the patient upload owner");
      }
      setSnapshot((current) => ({
        ...current,
        state: "filing",
        uploadId: source.uploadId,
        error: null,
      }));

      try {
        const resolvedSource = await resolveSource(source);
        const outcome = await waitForDocumentProcessingOutcome(
          client,
          patientId,
          resolvedSource.jobId!,
          resolvedSource.fileItemId!,
          options,
        );
        if (!outcome) {
          throw new Error("The document is still processing");
        }
        if (outcome.run.status !== "succeeded") {
          throw new Error(
            outcome.run.errorDetail || "The document could not be processed",
          );
        }
        const patientDocumentId =
          outcome.lookup.patientDocumentId ??
          (
            await waitForDocumentSourceIdentity(
              client,
              patientId,
              resolvedSource.jobId!,
              resolvedSource.fileItemId!,
              { attempts: 3, intervalMs: 250 },
            )
          ).patientDocumentId;
        setSnapshot({
          state: "complete",
          uploadId: source.uploadId,
          documentId: patientDocumentId,
          error: null,
        });
        return {
          ...outcome,
          patientDocumentId,
          sourceEntityId: outcome.lookup.sourceEntityId,
        };
      } catch (error) {
        const normalizedError =
          error instanceof Error
            ? error
            : new Error("Could not process document");
        setSnapshot((current) => ({
          ...current,
          state: "failed",
          uploadId: source.uploadId,
          error: normalizedError,
        }));
        throw normalizedError;
      }
    },
    [client, patientId, resolveSource],
  );

  const transfer = useCallback(
    async (
      file: DocumentSourceFile,
      options: FiledDocumentOptions,
    ): Promise<{ uploadId: string; documentId: string }> => {
      fileOptionsRef.current = options;
      const source = await uploadSource(file, options);
      const documentId = await fileSource(source, options);
      return { uploadId: source.uploadId, documentId };
    },
    [fileSource, uploadSource],
  );

  const retryFile = useCallback(async (): Promise<string> => {
    const source = sourceRef.current;
    const options = fileOptionsRef.current;
    if (!source || !options) {
      throw new Error("There is no document transfer to retry");
    }
    return fileSource(source, options);
  }, [fileSource]);

  const reset = useCallback(() => {
    sourceRef.current = null;
    sourcesByUploadIdRef.current.clear();
    fileOptionsRef.current = null;
    setSnapshot({
      state: "idle",
      uploadId: null,
      documentId: null,
      error: null,
    });
  }, []);

  return {
    ...snapshot,
    uploadSource,
    fileSource,
    processSource,
    transfer,
    retryFile,
    reset,
  };
}
